// web/src/shell/nav.ts — the single source of truth for the app's information
// architecture (M0.4). The persistent left nav, the breadcrumb resolver, and the
// router all read from here so the IA stays consistent. Routes mirror the
// REDESIGN-PLAN IA; the Vault is the landing route (preserves the existing
// console). Icons come from the one set (lucide-react) via the Icon wrapper.
import {
  Vault,
  Inbox,
  KeyRound,
  ScrollText,
  ClipboardCheck,
  Settings,
  type LucideIcon,
} from 'lucide-react';

export interface NavItem {
  /** Absolute route path. */
  path: string;
  /** Left-nav + breadcrumb label. */
  label: string;
  icon: LucideIcon;
  /** Short description for empty/placeholder pages + tooltips. */
  blurb: string;
}

// Primary navigation, in display order. "Vault" is index ("/").
export const NAV_ITEMS: readonly NavItem[] = [
  {
    path: '/',
    label: 'Vault',
    icon: Vault,
    blurb: 'Sealed records you initiated or hold a shard in — status, quorum health, members.',
  },
  {
    path: '/proofs',
    label: 'Shard proofs',
    icon: Inbox,
    blurb: 'From time to time the system asks you to prove you still hold your shard. Answer here — it proves key control and reveals nothing.',
  },
  {
    path: '/my-access',
    label: 'My shards',
    icon: KeyRound,
    blurb: 'Records you hold a shard in — quorum health and members. Nothing to do until an unseal is requested.',
  },
  {
    path: '/audit',
    label: 'Audit log',
    icon: ScrollText,
    blurb: 'Tamper-evident timeline — filter, verify the chain, and export.',
  },
  {
    path: '/responsibilities',
    label: 'Custody',
    icon: ClipboardCheck,
    blurb: 'Records you own and answer for: possession-proof status, ceremony history, and roster.',
  },
  {
    path: '/settings',
    label: 'Settings',
    icon: Settings,
    blurb: 'Your identity, the keys and devices registered to you, and what your role lets you do.',
  },
] as const;

export interface Crumb {
  label: string;
  path?: string;
}

/** Resolve breadcrumbs for a pathname against the IA. Always rooted at "Break Glass". */
export function crumbsFor(pathname: string): Crumb[] {
  const root: Crumb = { label: 'Break Glass', path: '/' };
  if (pathname === '/' || pathname === '') {
    return [root, { label: 'Vault' }];
  }
  const segments = pathname.split('/').filter(Boolean);
  const crumbs: Crumb[] = [root];
  let acc = '';
  for (const seg of segments) {
    acc += `/${seg}`;
    const match = NAV_ITEMS.find((n) => n.path === acc);
    crumbs.push({ label: match?.label ?? titleize(seg), path: acc });
  }
  // The last crumb is the current page — drop its link.
  if (crumbs.length > 1) delete crumbs[crumbs.length - 1].path;
  return crumbs;
}

function titleize(seg: string): string {
  return seg
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
